import {
  LoginDTO,
  LoginResponse,
  LoginResponseDTO,
  TwoFactorAuthDTO,
  UpdateUserDTO,
} from "@/lib/auth/auth-types";
import api from "@/lib/axiosConfig.ts";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import axios from "axios";

export async function login(authInfos: LoginDTO): Promise<LoginResponse> {
  try {
    const { data, status } = await api.post<LoginResponseDTO>(
      "/auth/signin?locale=" + authInfos.locale,
      {
        email: authInfos.email,
        password: authInfos.password,
      },
      {
        withCredentials: true,
      },
    );
    return {
      ...data,
      status,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status: status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
      };
    }
    throw error;
  }
}

export async function twoFactorAuth(
  authInfos: TwoFactorAuthDTO,
): Promise<LoginResponse> {
  try {
    const { data, status } = await api.post<LoginResponseDTO>(
      "/auth/2fa?locale=" + authInfos.locale,
      {
        email: authInfos.email,
        twoFactorToken: authInfos.twoFactorToken,
      },
      {
        withCredentials: true,
      },
    );
    return {
      ...data,
      status,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status: status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
      };
    }
    throw error;
  }
}

export async function resend2FA(email: string): Promise<{ status: number }> {
  const { status } = await api.post<LoginResponseDTO>(
    "/auth/2fa/resend",
    { email },
    {
      validateStatus: (code) =>
        (code >= 200 && code < 300) || code === HttpStatusCode.Gone,
    },
  );
  return { status };
}

async function updateUser({
  userId,
  dto,
}: {
  userId: string;
  dto: UpdateUserDTO;
}) {
  const { data } = await api.patch("/back/api/user/" + userId, dto);
  return data;
}

export const useUpdateUser = (token: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["user", token] }),
  });
};

async function getUser(token: string | null) {
  if (token) {
    const { data } = await api.get("/back/api/user/" + token);
    return data;
  }
}

export const useUser = (token: string | null) => {
  return useQuery({
    queryKey: ["user", token],
    queryFn: () => getUser(token),
    placeholderData: keepPreviousData,
    enabled: !!token,
  });
};

async function resetPassword(email: string, urlWebsite: string) {
  const { data } = await api.post(
    "/auth/reset?email=" + email + "&urlWebsite=" + urlWebsite,
  );
  return data;
}

export const useResetPassword = (email: string, urlWebsite: string) => {
  return useMutation({
    mutationFn: () => resetPassword(email, urlWebsite),
  });
};

interface RedeemPasswordDTO {
  user: {
    email: string;
    password: string;
    confirm_password: string;
  };
  token: string;
}

async function redeemPassword(redeemPasswordData: RedeemPasswordDTO) {
  const { data } = await api.post("/back/api/user/redeem", {
    ...redeemPasswordData,
  });
  return data;
}

export const useRedeemPassword = () => {
  return useMutation({
    mutationFn: (redeemPasswordData: RedeemPasswordDTO) =>
      redeemPassword(redeemPasswordData),
  });
};
